import {
  useState,
  useRef,
  useCallback,
  createContext,
  useContext,
  type ComponentType,
  type ReactNode,
} from "react";
import { trackFormView } from "@/lib/gtm";
import type { OffertFormModalProps } from "./OffertFormModal";

interface OffertContextType {
  openOffert: (source?: string) => void;
}

const OffertContext = createContext<OffertContextType>({
  openOffert: () => {},
});

export function useOffert() {
  return useContext(OffertContext);
}

/**
 * Provider stays on the homepage tree. The form (dialog, checkbox, sonner-adjacent UI)
 * loads only when someone opens the modal so it stays off the first JS budget.
 */
export function OffertProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("offert-form");
  const [Modal, setModal] =
    useState<ComponentType<OffertFormModalProps> | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const openOffert = useCallback((src?: string) => {
    opener.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const s = src || "offert-form";
    setSource(s);
    setOpen(true);
    trackFormView(s);
    void import("./OffertFormModal").then(mod => {
      setModal(() => mod.default);
    });
  }, []);

  return (
    <OffertContext.Provider value={{ openOffert }}>
      {children}
      {Modal ? (
        <Modal
          open={open}
          onOpenChange={setOpen}
          source={source}
          onCloseAutoFocus={event => {
            if (opener.current?.isConnected) {
              event.preventDefault();
              opener.current.focus();
            }
          }}
        />
      ) : null}
    </OffertContext.Provider>
  );
}
